import type { Chart as ChartJS, Plugin } from 'chart.js';

/**
 * The configuration-change marker overlay's canvas half (ENG-3109).
 *
 * It resolves every marker candidate against the category scale, paints one
 * vertical rule per density-visible marker group, and publishes the candidate
 * pixels so React can re-evaluate density after a resize. The glyph remains
 * a real DOM button positioned over the canvas by
 * `chart-config-marker-layer.tsx`, because a canvas cannot take focus, cannot
 * carry an accessible name, and cannot be reached from a keyboard at all.
 *
 * **Why a plugin at all**, when `chart-dominance.ts` sets the precedent that an
 * annotation should be a hidden-axis dataset rather than new machinery: that
 * precedent covers an annotation shaped like a series, and a horizontal
 * reference line is one. A vertical rule at a category boundary is not — it
 * would need one dataset per marker, twenty-five of them would enter the
 * legend, the tooltip and the datalabels collision resolver, and the stack
 * order of the real series would change underneath them. Painting is cheaper
 * and stays out of the data model entirely.
 *
 * **First-frame safety (ENG-3184).** That fix exists because a plugin read a
 * structure Chart.js had not built yet. Everything this plugin touches is
 * checked on every call rather than assumed from the hook it is in:
 * `chartArea`, the `x` scale, the pixel the scale returns, and the 2D context.
 * `afterDatasetsDraw` cannot run before a layout in practice — but "in
 * practice" is exactly the assumption ENG-3184 was created by, and
 * `chart-config-marker-plugin.test.ts` proves the guards by driving the hook
 * with each structure missing. Nothing here defers a render, and nothing here
 * touches `resizeDelay`, which ENG-3130 measured and owns.
 */

export const CONFIG_MARKER_PLUGIN_ID = 'minervaConfigMarkers';

/** Candidate positions on the category scale, in canvas-relative pixels. */
export type ConfigMarkerGeometry = {
  area: { top: number; bottom: number; left: number; right: number };
  /** One entry per in-range candidate, including density-hidden candidates. */
  points: readonly { bucketIndex: number; x: number }[];
};

export type ConfigMarkerPluginOptions = {
  /** Candidate bucket indices, ascending. `visible` controls paint, not geometry. */
  points: readonly {
    bucketIndex: number;
    emphasised: boolean;
    visible: boolean;
  }[];
  /** The rule colour. A neutral annotation token, never a series hue. */
  color: string;
  /**
   * The semantic paper colour painted as a casing behind the rule.
   *
   * Every chart series holds 3:1 against paper, while one neutral foreground
   * cannot hold 3:1 against both luminance bands of the categorical ramp. The
   * casing is optional: with no halo colour the rule paints on its own, which
   * is the path the density and geometry tests exercise.
   */
  haloColor?: string;
  /**
   * Called after painting, and ONLY when the candidate geometry differs from
   * the last call for this chart.
   *
   * The gate is what makes it safe to route this into React state. Chart.js
   * draws on every animation frame of a resize; an ungated callback would set
   * state sixty times a second, and each of those re-renders the chart's
   * parent. Compared rather than throttled because a converged chart must
   * produce exactly zero calls, not merely few — the size-ladder loop in
   * `chart-size-ladder.ts` is the same failure caught late.
   *
   * The layer it feeds is absolutely positioned and contributes no height, so
   * there is no path from a published geometry back to a different `chartArea`.
   */
  onGeometry?: (geometry: ConfigMarkerGeometry) => void;
};

/**
 * The last geometry published per chart, as its comparison string.
 *
 * A `WeakMap` keyed on the chart rather than a field written onto it, matching
 * `chart-render-after-first-update.ts`: a destroyed chart drops out on its own
 * and the plugin needs no teardown hook to avoid retaining one.
 */
const lastPublished = new WeakMap<ChartJS, string>();

const RULE_WIDTH_PX = 1;
const ROLLBACK_RULE_WIDTH_PX = 2;
const RULE_DASH: readonly number[] = [4, 3];
/*
 * The paper casing is wider than the rule by two pixels on each side, so a
 * heavier rollback rule keeps the same surround as an ordinary one. Expressed
 * as an extent added to whatever weight the rule paints at, rather than a fixed
 * casing width, precisely because emphasis is carried by weight here.
 */
const RULE_HALO_EXTENT_PX = 4;

export const configMarkerPlugin: Plugin = {
  id: CONFIG_MARKER_PLUGIN_ID,

  afterDatasetsDraw(chart, _args, options) {
    const resolved = options as Partial<ConfigMarkerPluginOptions> | undefined;
    const points = resolved?.points;

    /*
     * No options at all is the normal case: this plugin is registered globally
     * and every chart in the product that is not a Family 4 time series will
     * enter here on every frame.
     */
    if (
      !resolved ||
      !Array.isArray(points) ||
      typeof resolved.color !== 'string'
    ) {
      return;
    }

    const area = chart.chartArea;
    const scale = chart.scales?.x;
    const context = chart.ctx;

    if (!area || !scale || !context) {
      return;
    }

    /*
     * A zero-height or inverted plot box is what a chart reports before layout
     * and inside a collapsed grid cell. Painting into it is harmless but
     * publishing it is not: the DOM layer would place every glyph at the same
     * pixel, and the next real frame would move them all.
     */
    if (!(area.bottom > area.top) || !(area.right > area.left)) {
      return;
    }

    const resolvedPoints: { bucketIndex: number; x: number }[] = [];

    context.save();
    context.beginPath();
    context.rect(
      area.left,
      area.top,
      area.right - area.left,
      area.bottom - area.top
    );
    context.clip();
    /*
     * The casing only paints when a paper colour is supplied; both strokes set
     * their own `strokeStyle` per point, so nothing is assigned up here.
     */
    const haloColor =
      typeof resolved.haloColor === 'string' ? resolved.haloColor : null;

    for (const point of points) {
      const x = scale.getPixelForValue(point.bucketIndex);

      /*
       * A bucket index outside the rendered run — which a stale marker set is,
       * for the frame between a range change and its refetch — returns NaN or a
       * pixel beyond the plot. Skipped rather than clamped: a rule pinned to
       * the edge asserts a change happened at a time it did not.
       */
      if (!Number.isFinite(x) || x < area.left || x > area.right) {
        continue;
      }

      if (point.visible) {
        /*
         * Rollbacks carry more consequence, so emphasis is weight rather than
         * the former shorter dash, which measured lighter than the ordinary
         * rule in light mode and equal-weight in dark mode.
         */
        const lineWidth = point.emphasised
          ? ROLLBACK_RULE_WIDTH_PX
          : RULE_WIDTH_PX;
        const alignedX =
          lineWidth % 2 === 0 ? Math.round(x) : Math.round(x) + 0.5;
        context.setLineDash([...RULE_DASH]);
        context.beginPath();
        /* Odd widths centre on a half pixel; even widths centre on an integer. */
        context.moveTo(alignedX, area.top);
        context.lineTo(alignedX, area.bottom);
        /*
         * Casing first, foreground second, sharing one path and dash phase.
         * The paper casing paints two pixels wider per side and carries the
         * 3:1 boundary against every filled series; the neutral foreground
         * paints over its centre and keeps the annotation vocabulary and its
         * text pairing intact. Emphasis stays weight, not a second dash, so the
         * casing widens with the rule and the surround is constant.
         */
        if (haloColor) {
          context.lineWidth = lineWidth + RULE_HALO_EXTENT_PX;
          context.strokeStyle = haloColor;
          context.stroke();
        }
        context.lineWidth = lineWidth;
        context.strokeStyle = resolved.color;
        context.stroke();
      }

      /*
       * Density-hidden candidates still publish their true pixels. React needs
       * those positions to restore markers when a card grows; publishing only
       * painted points would make a narrow-card decision irreversible.
       */
      resolvedPoints.push({ bucketIndex: point.bucketIndex, x });
    }

    context.restore();

    const publish = resolved.onGeometry;
    if (!publish) {
      return;
    }

    const geometry: ConfigMarkerGeometry = {
      area: {
        top: area.top,
        bottom: area.bottom,
        left: area.left,
        right: area.right,
      },
      points: resolvedPoints,
    };

    const signature = JSON.stringify(geometry);
    if (lastPublished.get(chart) === signature) {
      return;
    }

    lastPublished.set(chart, signature);
    publish(geometry);
  },
};
